import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useImageGeneration } from '../../hooks/useImageGeneration';
import Icon from '../../components/AppIcon';
import Header from '../../components/navigation/Header';

// ─── Floating particle component ─────────────────────────────────────────────
const Particle = ({ delay, x, color, size }) => (
  <motion.div
    className="absolute rounded-full pointer-events-none"
    style={{ left: `${x}%`, top: '-10px', width: size, height: size, background: color }}
    initial={{ y: -20, opacity: 1, rotate: 0 }}
    animate={{ y: '110vh', opacity: [1, 1, 0], rotate: 720 }}
    transition={{ duration: 3.5 + Math.random() * 2, delay, ease: 'easeIn', repeat: Infinity, repeatDelay: Math.random() * 4 }}
  />
);

const PARTICLES = Array.from({ length: 28 }, (_, i) => ({
  id: i,
  delay: (i * 0.18) % 3,
  x: (i * 37 + 11) % 100,
  color: ['#f472b6', '#a78bfa', '#38bdf8', '#fb923c', '#34d399', '#fbbf24', '#e879f9']?.[i % 7],
  size: `${6 + (i % 5) * 3}px`,
}));

// ─── Shimmer ring decoration ──────────────────────────────────────────────────
const ShimmerRing = ({ size, delay, opacity }) => (
  <motion.div
    className="absolute rounded-full border-2 border-violet-400/30 pointer-events-none"
    style={{ width: size, height: size, top: '50%', left: '50%', x: '-50%', y: '-50%' }}
    initial={{ scale: 0.6, opacity: 0 }}
    animate={{ scale: [0.6, 1.2, 0.6], opacity: [0, opacity, 0] }}
    transition={{ duration: 3.5, delay, repeat: Infinity, ease: 'easeInOut' }}
  />
);

// ─── Person image card ────────────────────────────────────────────────────────
const PersonCard = ({ image, isLoading, error, label, side }) => {
  const isLeft = side === 'left';
  return (
    <motion.div
      initial={{ x: isLeft ? -80 : 80, opacity: 0, rotate: isLeft ? -6 : 6 }}
      animate={{ x: 0, opacity: 1, rotate: isLeft ? -3 : 3 }}
      transition={{ type: 'spring', stiffness: 80, damping: 18, delay: 0.6 }}
      className={`relative flex-shrink-0 ${isLeft ? 'self-end' : 'self-start'}`}
      style={{ width: 140, height: 200 }}
    >
      <div className="w-full h-full rounded-3xl overflow-hidden shadow-2xl border-2 border-white/40 bg-gradient-to-br from-violet-200 to-fuchsia-200">
        {isLoading ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
              className="w-8 h-8 border-3 border-violet-400 border-t-transparent rounded-full"
              style={{ borderWidth: 3 }}
            />
            <span className="text-xs text-violet-500 font-medium">Generating…</span>
          </div>
        ) : image ? (
          <img
            src={`data:image/png;base64,${image?.data?.[0]?.b64_json}`}
            alt={label}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name={isLeft ? 'User' : 'UserCheck'} size={48} color="#a78bfa" />
          </div>
        )}
      </div>
      {/* Label badge */}
      <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white rounded-full px-3 py-1 shadow-md border border-violet-100">
        <span className="text-xs font-semibold text-violet-600">{label}</span>
      </div>
    </motion.div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const CreditPurchaseSuccess = () => {
  const navigate = useNavigate();
  const [showContent, setShowContent] = useState(false);
  const hasGeneratedRef = useRef(false);

  const {
    image: maleImage,
    isLoading: maleLoading,
    error: maleError,
    generate: generateMale,
  } = useImageGeneration('GEMINI', 'gemini/gemini-2.5-flash-image');

  const {
    image: femaleImage,
    isLoading: femaleLoading,
    error: femaleError,
    generate: generateFemale,
  } = useImageGeneration('GEMINI', 'gemini/gemini-2.5-flash-image');

  useEffect(() => {
    const t = setTimeout(() => setShowContent(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (hasGeneratedRef?.current) return;
    hasGeneratedRef.current = true;

    generateMale(
      'A confident, stylish young man in his late 20s, smiling warmly, wearing smart casual clothes, soft studio lighting, clean white background, portrait style, photorealistic',
      { size: '896x1280' }
    );
    generateFemale(
      'A radiant, confident young woman in her late 20s, smiling joyfully, wearing elegant casual attire, soft studio lighting, clean white background, portrait style, photorealistic',
      { size: '896x1280' }
    );
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden flex flex-col items-center justify-center px-4 py-16"
      style={{ background: 'linear-gradient(135deg, #1e0533 0%, #2d1060 30%, #0f172a 60%, #1a0a3e 100%)' }}
    >
      <Header
        user={null}
        onNavigate={(path) => navigate(path)}
        onPurchaseCredits={() => navigate('/pricing')}
        onLogout={() => navigate('/')}
      />
      {/* Animated background orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute rounded-full"
          style={{ width: 600, height: 600, top: '-15%', left: '-10%', background: 'radial-gradient(circle, rgba(167,139,250,0.18) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute rounded-full"
          style={{ width: 500, height: 500, bottom: '-10%', right: '-8%', background: 'radial-gradient(circle, rgba(244,114,182,0.15) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
        />
        <motion.div
          className="absolute rounded-full"
          style={{ width: 350, height: 350, top: '40%', left: '40%', background: 'radial-gradient(circle, rgba(56,189,248,0.1) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
        />
      </div>
      {/* Shimmer rings */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
        <ShimmerRing size={320} delay={0} opacity={0.5} />
        <ShimmerRing size={480} delay={1} opacity={0.3} />
        <ShimmerRing size={640} delay={2} opacity={0.15} />
      </div>
      {/* Confetti particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {PARTICLES?.map(p => <Particle key={p?.id} {...p} />)}
      </div>
      <AnimatePresence>
        {showContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="relative z-10 w-full max-w-2xl flex flex-col items-center"
          >
            {/* People images row */}
            <div className="w-full flex items-end justify-between mb-8 px-4 gap-4">
              <PersonCard image={maleImage} isLoading={maleLoading} error={maleError} label="Him" side="left" />

              {/* Center trophy / star burst */}
              <motion.div
                className="flex flex-col items-center gap-2 flex-1"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 120, damping: 14, delay: 0.3 }}
              >
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.08, 1] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                  className="relative"
                >
                  {/* Glow behind trophy */}
                  <div className="absolute inset-0 rounded-full blur-2xl opacity-70"
                    style={{ background: 'radial-gradient(circle, #f472b6 0%, #a78bfa 60%, transparent 100%)', transform: 'scale(1.5)' }}
                  />
                  <div className="relative w-20 h-20 rounded-full flex items-center justify-center shadow-2xl"
                    style={{ background: 'linear-gradient(135deg, #f472b6, #a78bfa, #38bdf8)' }}
                  >
                    <span className="text-4xl">🏆</span>
                  </div>
                </motion.div>
                <div className="flex gap-1">
                  {['⭐', '✨', '⭐']?.map((s, i) => (
                    <motion.span key={i} className="text-lg"
                      animate={{ y: [0, -6, 0] }}
                      transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.2 }}
                    >{s}</motion.span>
                  ))}
                </div>
              </motion.div>

              <PersonCard image={femaleImage} isLoading={femaleLoading} error={femaleError} label="Her" side="right" />
            </div>

            {/* Main card */}
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="w-full rounded-3xl overflow-hidden shadow-2xl"
              style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.12)' }}
            >
              {/* Top gradient stripe */}
              <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #f472b6, #a78bfa, #38bdf8, #34d399)' }} />

              <div className="px-8 py-10 text-center">
                {/* Congrats heading */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.55, type: 'spring', stiffness: 140 }}
                >
                  <p className="text-sm font-semibold tracking-[0.25em] uppercase mb-3"
                    style={{ color: '#f472b6', fontFamily: 'DM Sans, sans-serif' }}
                  >
                    🎉 Purchase Complete
                  </p>
                  <h1 className="text-4xl md:text-5xl font-extrabold mb-3 leading-tight"
                    style={{
                      fontFamily: 'Manrope, DM Sans, sans-serif',
                      background: 'linear-gradient(135deg, #f9fafb 0%, #e9d5ff 40%, #bae6fd 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    You're Glowing Up! ✨
                  </h1>
                  <p className="text-base md:text-lg leading-relaxed max-w-md mx-auto"
                    style={{ color: 'rgba(255,255,255,0.65)', fontFamily: 'DM Sans, sans-serif' }}
                  >
                    Your credits are locked and loaded. Time to find out exactly where you rank — and own it.
                  </p>
                </motion.div>

                {/* Credits badge */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.75, type: 'spring', stiffness: 160 }}
                  className="inline-flex items-center gap-2.5 rounded-2xl px-6 py-3 mt-6 mb-8"
                  style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.25), rgba(244,114,182,0.2))', border: '1px solid rgba(167,139,250,0.4)' }}
                >
                  <motion.span
                    animate={{ rotate: [0, 20, -20, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, delay: 1 }}
                    className="text-2xl"
                  >⚡</motion.span>
                  <span className="text-base font-bold" style={{ color: '#e9d5ff', fontFamily: 'Manrope, sans-serif' }}>
                    Credits added to your account
                  </span>
                  <motion.span
                    animate={{ rotate: [0, -20, 20, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, delay: 1.3 }}
                    className="text-2xl"
                  >⚡</motion.span>
                </motion.div>

                {/* Divider */}
                <div className="w-full h-px mb-8" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)' }} />

                {/* CTA Buttons */}
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.9, duration: 0.5 }}
                  className="flex flex-col sm:flex-row gap-4 justify-center"
                >
                  {/* Dashboard button */}
                  <motion.button
                    whileHover={{ scale: 1.04, y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => navigate('/user-dashboard')}
                    className="flex items-center justify-center gap-2.5 px-8 py-4 rounded-2xl font-bold text-base transition-all duration-200 shadow-xl"
                    style={{
                      background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                      color: '#fff',
                      fontFamily: 'Manrope, sans-serif',
                      boxShadow: '0 8px 32px rgba(124,58,237,0.45)',
                    }}
                  >
                    <Icon name="LayoutDashboard" size={20} color="#fff" />
                    My Dashboard
                  </motion.button>

                  {/* Upload photos button */}
                  <motion.button
                    whileHover={{ scale: 1.04, y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => navigate('/photo-upload-interface')}
                    className="flex items-center justify-center gap-2.5 px-8 py-4 rounded-2xl font-bold text-base transition-all duration-200 shadow-xl"
                    style={{
                      background: 'linear-gradient(135deg, #ec4899, #f472b6)',
                      color: '#fff',
                      fontFamily: 'Manrope, sans-serif',
                      boxShadow: '0 8px 32px rgba(236,72,153,0.4)',
                    }}
                  >
                    <Icon name="Camera" size={20} color="#fff" />
                    Rank My Photos
                  </motion.button>
                </motion.div>

                {/* Footer note */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                  className="mt-7 text-xs"
                  style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'DM Sans, sans-serif' }}
                >
                  <Icon name="ShieldCheck" size={12} color="rgba(255,255,255,0.35)" className="inline mr-1" />
                  Payment secured by Stripe · Credits never expire
                </motion.p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreditPurchaseSuccess;
